import { Box, TextField, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { FONT_FAMILY } from './shared/index.js';

type OtpInputProps = {
  readonly fieldNumber: number;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly isError?: boolean;
  readonly isDisabled?: boolean;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: 40,
    width: 40,
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'error.main',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
    },
    '& input': {
      fontFamily: FONT_FAMILY,
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: '0.15px',
      lineHeight: '24px',
      padding: 0,
      textAlign: 'center',
    },
  },
}));

const OtpContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-start',
  marginTop: 0,
});

const makeEmptyOtp = (length: number): string[] => Array.from({ length }, () => '');

const parseOtp = (value: string, fieldNumber: number): string[] => {
  const digits = value.split('').slice(0, fieldNumber);
  return [...digits, ...makeEmptyOtp(Math.max(0, fieldNumber - digits.length))];
};

const OtpInput = ({
  fieldNumber,
  value,
  onChange,
  isError = false,
  isDisabled = false,
}: OtpInputProps) => {
  const [otp, setOtp] = useState<string[]>(() =>
    value ? parseOtp(value, fieldNumber) : makeEmptyOtp(fieldNumber)
  );

  const inputReferences = useRef<Array<HTMLInputElement | undefined>>([]);

  useEffect(() => {
    if (value !== otp.join('')) {
      setOtp(value ? parseOtp(value, fieldNumber) : makeEmptyOtp(fieldNumber));
    }
  }, [value, fieldNumber, otp]);

  const handleChange = (index: number, inputValue: string) => {
    const digit = inputValue.replace(/\D/g, '').slice(-1);
    const newOtp = otp.map((current, position) => (position === index ? digit : current));
    setOtp(newOtp);
    onChange(newOtp.join(''));

    if (digit && index < fieldNumber - 1) {
      inputReferences.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = otp.map((current, position) => (position === index ? '' : current));
        setOtp(newOtp);
        onChange(newOtp.join(''));
      } else if (index > 0) {
        const newOtp = otp.map((current, position) => (position === index - 1 ? '' : current));
        setOtp(newOtp);
        onChange(newOtp.join(''));
        inputReferences.current[index - 1]?.focus();
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputReferences.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < fieldNumber - 1) {
      inputReferences.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData) {
      const digits = pastedData.slice(0, fieldNumber).split('');
      const newOtp = [...digits, ...makeEmptyOtp(Math.max(0, fieldNumber - digits.length))];
      setOtp(newOtp);
      onChange(newOtp.join(''));

      const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
      const focusIndex = nextEmptyIndex === -1 ? fieldNumber - 1 : nextEmptyIndex;
      inputReferences.current[focusIndex]?.focus();
    }
  };

  /* eslint-disable react/no-array-index-key -- OTP cells are positional; no stable key exists */
  return (
    <OtpContainer>
      {otp.map((digit, index) => (
        <StyledTextField
          key={index}
          disabled={isDisabled}
          error={isError}
          inputRef={(element: HTMLInputElement | undefined) => {
            // eslint-disable-next-line @silverhand/fp/no-mutation -- ref array requires imperative assignment
            inputReferences.current[index] = element;
          }}
          slotProps={{ input: { autoComplete: 'off' } }}
          value={digit}
          variant="outlined"
          onChange={(event) => {
            handleChange(index, event.target.value);
          }}
          onKeyDown={(event) => {
            handleKeyDown(index, event);
          }}
          onPaste={handlePaste}
        />
      ))}
    </OtpContainer>
  );
  /* eslint-enable react/no-array-index-key */
};

export default OtpInput;
